import React, { useState, useRef } from 'react';
import { CaseStudyItem } from '@/types/case-study';
import { convertSupabaseUrlToRenderUrl } from '@/utils/imageUtils';

interface CaseStudyGalleryItemProps {
  caseStudy: CaseStudyItem;
  aspectRatioClass?: string;
  yearText?: string;
  usernameSlug?: string;
  onClick?: () => void;
  shouldShowBlur?: boolean;
}

const CaseStudyGalleryItem: React.FC<CaseStudyGalleryItemProps> = ({
  caseStudy,
  aspectRatioClass = 'aspect-video',
  yearText,
  usernameSlug,
  onClick,
  shouldShowBlur = false
}) => {
  const [retryAttempts, setRetryAttempts] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle image load error with retry
  const handleImageError = () => {
    if (retryAttempts < 2) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        setRetryAttempts(prev => prev + 1);
      }, 1000 * (retryAttempts + 1));
    }
  };

  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const imageUrl = caseStudy.cover_image ? convertSupabaseUrlToRenderUrl(caseStudy.cover_image) : null;
  const hasError = retryAttempts >= 2;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div className="flex flex-col cursor-pointer w-full mb-2 h-full">
      <div 
        className="flex flex-col overflow-hidden bg-white rounded-lg border shadow-sm dark:border-gray-800 hover:shadow-md transition-all duration-300 hover:-translate-y-1 h-full"
      >
        <div 
          className="relative overflow-hidden bg-background rounded-t-lg" 
          onClick={handleClick}
        >
          <div
            className={`${aspectRatioClass} relative flex items-center justify-center overflow-hidden ${!shouldShowBlur ? 'bg-muted' : ''}`}
            style={
              imageUrl && shouldShowBlur
                ? {
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            {imageUrl && !hasError && (
              <img
                src={`${imageUrl}${retryAttempts > 0 ? `?retry=${retryAttempts}` : ''}`}
                alt={caseStudy.title || "Case Study"}
                className={`relative z-10 ${shouldShowBlur ? 'w-[65%] h-auto object-contain rounded-lg shadow-[0_0_25px_rgba(0,0,0,0.08),0_0_50px_rgba(0,0,0,0.05)]' : 'w-full h-full object-cover'}`}
                onError={handleImageError}
                loading="lazy"
              />
            )}
            {hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <svg className="w-12 h-12 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p className="text-xs text-muted-foreground mt-2">Image unavailable</p>
              </div>
            )}
            {imageUrl && shouldShowBlur && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur" aria-hidden="true" />
            )}
          </div>
        </div>
        <div className="px-3 py-2 bg-white border-t border-gray-100 rounded-b-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-medium line-clamp-1 text-ellipsis"><a href={`/case-study/${encodeURIComponent(caseStudy.slug || caseStudy.id)}`} onClick={event => { if (onClick && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onClick(); } }}>{caseStudy.title || 'Untitled Case Study'}</a></h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-500"></div>
                  <span className="text-xs text-muted-foreground">Case Study</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyGalleryItem;
